import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Arr,
  sanitizeArr
} from '../../../lib/Array.lib';
import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import Core from '../../../lib/Core';
import {
  STATE_ACTIVE
} from '../../../lib/Definition';
import globalErrorHandler from '../../../lib/Error/globalErrorHandler.fun';
import {
  Fun
} from '../../../lib/Function.lib';
import useState from '../../../lib/hooks/useState.hook';
import {
  getPersonName,
  Obj
} from '../../../lib/Object.lib';
import {
  ROLE__EMPLOYER_RECRUITER,
  ROLE__LIMITED_RECRUITER,
  ROLE__SYS_ADMIN,
  ROLE__TRUSTED_RECRUITER
} from '../../../lib/services/Accounts/Session.lib';
import {
  COLLECTION__ACCOUNTS,
  readLoopbackRecord
} from '../../../lib/services/BE/loopback.api';
import {
  joinKeyName,
  Str,
  trim
} from '../../../lib/String.lib';
import {
  joinClassName
} from '../../Layout/Libraries/Theme.lib';
import Box from '../../Layout/Wrappers/Box';
import Button from '../../Layout/Wrappers/Button';
import Checkbox from '../../Layout/Wrappers/Checkbox';
import Fieldset from '../../Layout/Wrappers/Fieldset';
import IconButton from '../../Layout/Wrappers/IconButton';
import List from '../../Layout/Wrappers/List';
import Message from '../../Layout/Wrappers/Message';
import {
  PLACEMENT__TOP_END
} from '../../Layout/Wrappers/StyledTooltip';
import TextField from '../../Layout/Wrappers/TextField';
import Typography, {
  Label
} from '../../Layout/Wrappers/Typography';

const configurationOfRoles = {
  [ROLE__SYS_ADMIN]: { order: 1, className: 'd-none' },
  [ROLE__EMPLOYER_RECRUITER]: { order: 2, className: 'd-none' },
  [ROLE__TRUSTED_RECRUITER]: { order: 3, className: 'd-none' },
  [ROLE__LIMITED_RECRUITER]: { order: 4, className: 'c-orange' },
};

export function getAccountSelectorSortingKey(account) {
  return joinKeyName([
    account.firstName,
    account.lastName,
    account.email,
    // Following is really not necessary now,
    // but it can be useful for future re-ordering.
    configurationOfRoles[account.role].order,
  ]);
}

/**
 * Shows a dialog with a list of active accounts, and a button to
 * confirm the selection. When the user clicks the confirm button,
 * the `onApply` callback is called with the selected account(s) as
 * its argument.
 *
 * @param {Object} options
 * @param {Function} options.onApply - A callback that is called with
 * the selected account(s) when the user clicks the confirm button.
 * If not provided, defaults to a no-op function.
 */
export default async function openDialogAccountSelector({
  title = 'Select account',
  where = {},
  onApply = Fun
}) {
  try {
    const accounts = Arr(
      await readLoopbackRecord({
        collection: COLLECTION__ACCOUNTS,
        where: {
          state: STATE_ACTIVE,
          ...where
        },
        fields: [
          'id',
          'firstName',
          'lastName',
          'email',
          'role',
        ],
        order: [
          'role DESC',
          'firstName ASC',
          'lastName ASC',
          'email ASC'
        ]
      })
    ).sort(
      // order does not support case-insensitive sort
      // so we need to do an extra sort
      (a, b) => getAccountSelectorSortingKey(
        a
      ).localeCompare(
        getAccountSelectorSortingKey(b)
      )
    );
    Core.dialog.open({
      title,
      content: (
        <SelectorAccounts
          accounts={accounts}
        />
      ),
      actionBar: (
        <ActionBarAccountSelector />
      ),
      actions: [
        <Button outlined minW120
          label='Cancel'
        />,
        <Button primary minW120
          label='Apply'
          onClick={(event) => {
            const selectedIds = Arr(getControllerSelectedAccount().state?.selectedIds);
            onApply(selectedIds);
          }}
        />,
      ],
      paperStyle: {
        minWidth: '50%',
        maxWidth: 'calc(100% - 2rem)',
      },
    });
  }
  catch (error) {
    globalErrorHandler(error);
  }
}

export function SelectorAccounts({
  accounts,
}) {
  const [state, updateState] = useState();
  setControllerSelectedAccount({
    state,
    updateState,
  });
  const VALUE__FILTER = trim(state.filter);
  const DATA__ACCOUNTS = Arr(accounts).map(
    (account) => ({
      ...account,
      VALUE__SEARCH: joinClassName([
        account.firstName,
        account.lastName,
        account.email,
        account.role,
      ]),
      VALUE__LABEL: (
        <Box row flex1>
          <Typography bold className='mr-1'>
            {getPersonName(account)}
          </Typography>
          <Typography sub className='c-gray mr-1'>
            ({account.email})
          </Typography>
          <Typography sub className={configurationOfRoles[account.role].className}>
            {account.role}
          </Typography>
        </Box>
      ),
    })
  )
  const DATA__COLLECTION = DATA__ACCOUNTS.filter(
    (account) => new RegExp(VALUE__FILTER, 'i').test(account.VALUE__SEARCH)
  );
  const DATA__SELECTED_IDS = Arr(state.selectedIds);

  const YES__SELECTED = YES(DATA__SELECTED_IDS.length);

  const NOT__SELECTED = NOT(YES__SELECTED);

  return (
    <>
      <Fieldset
        title='Selected'
        wrapperProps={{
          className: 'mx-2'
        }}
        endDivider
      >
        <Message show={NOT__SELECTED}>
          No one selected
        </Message>
        {YES__SELECTED &&
          <ul>
            {DATA__ACCOUNTS.filter(
              (account) => DATA__SELECTED_IDS.includes(account.id)
            ).map((account, index) => (
              <li key={joinKeyName([
                'recruiter_to_notify',
                account.id,
                index
              ])}>
                <span>
                  {account.VALUE__LABEL}
                </span>
              </li>
            )
            )}
          </ul>
        }
      </Fieldset>
      <List>
        {DATA__COLLECTION.map((
          account,
          index,
          array,
          YES__CHECKED = DATA__SELECTED_IDS.includes(account.id),
        ) => (
          <ListItem
            key={joinKeyName([
              'SelectorAccounts',
              account.id,
              index
            ])}
            className='py-0'
          >
            <ListItemButton dense
              onClick={(event) => {
                const selectedIds = YES__CHECKED
                  ? DATA__SELECTED_IDS.filter((id) => id !== account.id)
                  : sanitizeArr([...DATA__SELECTED_IDS, account.id]);
                updateState({ selectedIds });
              }}
            >
              <ListItemIcon>
                <Checkbox
                  checked={DATA__SELECTED_IDS.includes(account.id)}
                />
              </ListItemIcon>
              <ListItemText>
                {account.VALUE__LABEL}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );
}

export function ActionBarAccountSelector() {
  const [state, updateState] = useState();
  const VALUE__FILTER = Str(state.filter);
  const YES__DISPLAY_CLEAR = YES(trim(state.filter));
  const _onChange = async (filter) => {
    await updateState({ filter });
    getControllerSelectedAccount().updateState({ filter });
  }
  return (
    <Box row>
      <Label className='no-truncate mx-1'>
        Filter
      </Label>
      <TextField disabledBlurExport
        placeholder='Type to filter list'
        value={VALUE__FILTER}
        onChange={async (event) => _onChange(event.target.value)}
        className='mr-1'
      />
      <IconButton
        acl={YES__DISPLAY_CLEAR}
        icon='cancel'
        className='icon16'
        onClick={async (event) => _onChange('')}
        title='Clear filter'
        placement={PLACEMENT__TOP_END}
      />
    </Box>
  );
}

const KEY__CONTROLLER__SELECTED_ACCOUNT = 'SelectedAccountController';
export function setControllerSelectedAccount(controller) {
  Core.setKeyValue(KEY__CONTROLLER__SELECTED_ACCOUNT, controller);
}
export function getControllerSelectedAccount() {
  return Obj(Core.getKeyValue(KEY__CONTROLLER__SELECTED_ACCOUNT));
}
